import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../context';
import { AudioField } from './AudioField';

type StageKind = 'shift_group' | 'users' | 'phone' | 'voicemail' | 'ivr' | 'time';
interface Stage {
  kind: StageKind;
  config: Record<string, unknown>;
  timeoutSeconds?: number;
  announcementBlob?: string | null;
}
interface IvrOption { digit: string; label: string; stages: Stage[] }
interface TimeEntry { label: string; days: number[]; start: string; end: string; stages: Stage[] }
interface Team { id: string; displayName: string }
interface SchedulingGroup { id: string; displayName: string; members: number }

const KIND_LABEL: Record<StageKind, string> = {
  shift_group: 'On-shift group', users: 'Specific people', phone: 'Phone number', voicemail: 'Voicemail', ivr: 'Menu (press a key)', time: 'Time condition',
};
const IVR_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'];
const DAY_LABEL: Record<number, string> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
const WEEKDAYS = [1, 2, 3, 4, 5];

/**
 * Ordered escalation plan for a line: ring stage 1's targets, on no-answer advance to the
 * next, ending in voicemail. shift_group rings everyone on shift in a Teams Shifts group.
 * A Menu (IVR) stage plays a recording and routes by the key pressed; each key (and "no key")
 * has its own ordered stages, after which the plan continues with whatever follows the menu.
 * A Time condition routes by the UK wall clock: ordered entries (days + start/end, first match
 * wins), each with its own stages (a menu allowed), plus an optional "otherwise" branch.
 */
export function RoutingPlanEditor({ lineId, onClose }: { lineId: string; onClose?: () => void }) {
  const api = useApi();
  const [stages, setStages] = useState<Stage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    api.get<{ kind: StageKind; config: Record<string, unknown>; timeout_seconds: number; announcement_blob: string | null }[]>(`/api/admin/lines/${lineId}/stages`)
      .then((rows) => setStages(rows.map((r) => ({ kind: r.kind, config: r.config, timeoutSeconds: r.timeout_seconds, announcementBlob: r.announcement_blob }))),
        (e: Error) => setError(e.message));
  }, [api, lineId]);
  useEffect(load, [load]);

  async function save() {
    setError(null);
    try {
      await api.put(`/api/admin/lines/${lineId}/stages`, { stages: stages.map(normaliseStage) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="picker">
      {onClose && (
        <div className="filters">
          <strong>Routing plan</strong>
          <span className="spacer" />
          <button className="link" onClick={onClose}>close</button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      <StageList stages={stages} onChange={setStages} allowMenu allowTime />
      <div className="filters">
        <button className="primary" onClick={save}>Save plan</button>
        {saved && <span className="hint">Saved — the bot picks it up within a few seconds.</span>}
      </div>
    </div>
  );
}

/** An empty "no key" / "otherwise" branch means "continue after this stage": send null. */
function normaliseStage(st: Stage): Stage {
  if (st.kind === 'ivr') {
    const noInput = st.config['noInput'] as { stages?: Stage[] } | null | undefined;
    return { ...st, config: { ...st.config, noInput: noInput && (noInput.stages ?? []).length > 0 ? noInput : null } };
  }
  if (st.kind === 'time') {
    const otherwise = st.config['otherwise'] as { stages?: Stage[] } | null | undefined;
    const entries = ((st.config['entries'] as TimeEntry[] | undefined) ?? []).map((e) => ({ ...e, stages: (e.stages ?? []).map(normaliseStage) }));
    return { ...st, config: { ...st.config, entries, otherwise: otherwise && (otherwise.stages ?? []).length > 0 ? { stages: otherwise.stages!.map(normaliseStage) } : null } };
  }
  return st;
}

type Updater<T> = (prev: T) => T;

/** Swap items i and i+d (no-op at the ends). Used for stages, menu options and time entries. */
function swap<T>(arr: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const n = [...arr]; [n[i], n[j]] = [n[j]!, n[i]!]; return n;
}

/**
 * The optional catch-all branch of a menu ("no key pressed") or time condition ("otherwise"):
 * either absent (the plan continues after the stage) or a list of stages of its own.
 */
function OptionalBranch({ title, whenAbsent, whenPresent, stages, onToggle, onChange, allowMenu }: {
  title: string;
  whenAbsent: string;
  whenPresent: string;
  /** null = absent */
  stages: Stage[] | null;
  onToggle: (present: boolean) => void;
  onChange: (update: Updater<Stage[]>) => void;
  allowMenu: boolean;
}) {
  return (
    <div className="picker" style={{ marginTop: '0.5rem' }}>
      <div className="filters">
        <strong>{title}</strong>
        <span className="sub">{stages ? whenPresent : whenAbsent}</span>
        <span className="spacer" />
        {stages
          ? <button className="link danger" onClick={() => onToggle(false)}>continue after this stage instead</button>
          : <button className="link" onClick={() => onToggle(true)}>route somewhere specific</button>}
      </div>
      {stages && <StageList stages={stages} onChange={onChange} allowMenu={allowMenu} compact />}
      {stages && stages.length === 0 && <p className="hint">Empty: saving will treat this as “continue with the stages after this stage”.</p>}
    </div>
  );
}

/**
 * An ordered list of stages with reorder/remove/add; used for the plan and for each menu branch.
 * All edits are expressed as functional updaters so a slow async edit (an audio upload that
 * resolves seconds later) merges into the latest state instead of overwriting newer edits.
 */
function StageList({ stages, onChange, allowMenu, allowTime, compact }: {
  stages: Stage[];
  onChange: (update: Updater<Stage[]>) => void;
  /** Menus may not nest inside menus (a time entry may hold one). */
  allowMenu: boolean;
  /** Time conditions live only in the top-level plan. */
  allowTime?: boolean;
  compact?: boolean;
}) {
  const update = (i: number, patch: Partial<Stage>) => onChange((prev) => prev.map((st, j) => (j === i ? { ...st, ...patch } : st)));
  const updateStage = (i: number, f: Updater<Stage>) => onChange((prev) => prev.map((st, j) => (j === i ? f(st) : st)));
  const move = (i: number, d: -1 | 1) => onChange((prev) => swap(prev, i, d));
  const kinds = (Object.keys(KIND_LABEL) as StageKind[]).filter((k) => (allowMenu || k !== 'ivr') && (allowTime || k !== 'time'));
  const freshConfig = (k: StageKind): Record<string, unknown> =>
    k === 'ivr' ? { options: [], noInput: null }
      : k === 'time' ? { timeZone: 'Europe/London', entries: [{ label: 'Office hours', days: WEEKDAYS, start: '08:00', end: '18:00', stages: [] }], otherwise: null }
        : {};

  return (
    <>
      <ol className="kb">
        {stages.map((st, i) => (
          <li key={i}>
            <span className="reorder">
              <button className="link" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
              <button className="link" disabled={i === stages.length - 1} onClick={() => move(i, 1)}>↓</button>
            </span>
            <div style={{ width: '100%' }}>
              <div className="filters">
                <select value={st.kind} onChange={(e) => update(i, { kind: e.target.value as StageKind, config: freshConfig(e.target.value as StageKind) })}>
                  {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
                {st.kind !== 'voicemail' && st.kind !== 'ivr' && st.kind !== 'time' && (
                  <label>rings for <input type="number" min={5} max={600} style={{ width: '4.5rem' }}
                    value={st.timeoutSeconds ?? 30} onChange={(e) => update(i, { timeoutSeconds: Number(e.target.value) })} /> s</label>
                )}
                <button className="link danger" onClick={() => onChange((prev) => prev.filter((_, j) => j !== i))}>remove</button>
              </div>
              {st.kind === 'shift_group' && <ShiftGroupConfig config={st.config} onChange={(cfg) => update(i, { config: cfg })} />}
              {st.kind === 'phone' && (
                <input placeholder="+44..." value={String(st.config['number'] ?? '')} onChange={(e) => update(i, { config: { number: e.target.value } })} />
              )}
              {st.kind === 'users' && (
                <input style={{ width: '100%' }} placeholder="user:<oid>,user:<oid>" value={String(st.config['targets'] ?? '')}
                  onChange={(e) => update(i, { config: { targets: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) } })} />
              )}
              {st.kind === 'voicemail' && (
                <>
                  <span className="sub">
                    Takes a message from the caller (played back on the call). The line greeting below is played first, then the
                    caller is handed to voicemail, which asks them to leave a message after the tone.
                  </span>
                  <AudioField
                    label="Line greeting — played to the caller just before they are handed to voicemail (optional)"
                    blob={st.announcementBlob ?? null}
                    onChange={(b) => update(i, { announcementBlob: b })}
                  />
                </>
              )}
              {st.kind === 'ivr' && (
                <IvrConfigEditor stage={st} onChange={(f) => updateStage(i, f)} />
              )}
              {st.kind === 'time' && (
                <TimeConfigEditor stage={st} onChange={(f) => updateStage(i, f)} />
              )}
              {st.kind !== 'voicemail' && st.kind !== 'ivr' && st.kind !== 'time' && !compact && (
                <AudioField
                  label={i === 0 ? 'Message before ringing (optional)' : 'Message before this transfer (optional)'}
                  blob={st.announcementBlob ?? null}
                  onChange={(b) => update(i, { announcementBlob: b })}
                />
              )}
            </div>
          </li>
        ))}
      </ol>
      <div className="filters">
        <button onClick={() => onChange((prev) => [...prev, { kind: 'shift_group', config: {}, timeoutSeconds: 30 }])}>+ Add stage</button>
      </div>
    </>
  );
}

/** Menu recording, key options (each with its own branch) and the "no key pressed" branch. */
function IvrConfigEditor({ stage, onChange }: { stage: Stage; onChange: (update: Updater<Stage>) => void }) {
  const cfg = stage.config;
  const optionsOf = (st: Stage) => (Array.isArray(st.config['options']) ? st.config['options'] : []) as IvrOption[];
  const options = optionsOf(stage);
  const noInput = (cfg['noInput'] as { stages: Stage[] } | null | undefined) ?? null;
  const setCfg = (patch: Record<string, unknown>) => onChange((prev) => ({ ...prev, config: { ...prev.config, ...patch } }));
  const setOptions = (f: Updater<IvrOption[]>) => onChange((prev) => ({ ...prev, config: { ...prev.config, options: f(optionsOf(prev)) } }));
  const setOption = (i: number, patch: Partial<IvrOption>) => setOptions((opts) => opts.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const setOptionStages = (i: number, f: Updater<Stage[]>) => setOptions((opts) => opts.map((o, j) => (j === i ? { ...o, stages: f(o.stages ?? []) } : o)));
  const setNoInputStages = (f: Updater<Stage[]>) => onChange((prev) => {
    const cur = (prev.config['noInput'] as { stages?: Stage[] } | null | undefined)?.stages ?? [];
    return { ...prev, config: { ...prev.config, noInput: { stages: f(cur) } } };
  });
  const usedKeys = new Set(options.map((o) => o.digit));
  const nextKey = IVR_KEYS.find((k) => !usedKeys.has(k));

  return (
    <div className="picker">
      <AudioField
        label="Menu recording — e.g. “Press 1 for clinical, 2 for IT…” (required: the plan will not save without it)"
        blob={stage.announcementBlob ?? null}
        onChange={(b) => onChange((prev) => ({ ...prev, announcementBlob: b }))}
      />
      <div className="filters">
        <label>wait <input type="number" min={1} max={60} style={{ width: '4rem' }}
          value={Number(cfg['inputTimeoutSeconds'] ?? 5)} onChange={(e) => setCfg({ inputTimeoutSeconds: Number(e.target.value) })} /> s for a key after the recording</label>
        <label>play the menu up to <input type="number" min={1} max={5} style={{ width: '3.5rem' }}
          value={Number(cfg['maxPlays'] ?? 2)} onChange={(e) => setCfg({ maxPlays: Number(e.target.value) })} /> times</label>
      </div>

      {options.length === 0 && <p className="hint">Add at least one key option — a menu with no options will not save.</p>}
      {options.map((o, i) => (
        <div key={i} className="picker" style={{ marginTop: '0.5rem' }}>
          <div className="filters">
            <strong>Key</strong>
            <select value={o.digit} onChange={(e) => setOption(i, { digit: e.target.value })}>
              {IVR_KEYS.map((k) => <option key={k} value={k} disabled={k !== o.digit && usedKeys.has(k)}>{k}</option>)}
            </select>
            <input placeholder="Label (e.g. Clinical)" value={o.label} onChange={(e) => setOption(i, { label: e.target.value })} />
            <span className="spacer" />
            <button className="link danger" onClick={() => setOptions((opts) => opts.filter((_, j) => j !== i))}>remove option</button>
          </div>
          <StageList stages={o.stages ?? []} onChange={(f) => setOptionStages(i, f)} allowMenu={false} compact />
        </div>
      ))}
      <div className="filters">
        <button disabled={!nextKey} onClick={() => nextKey && setOptions((opts) => [...opts, { digit: nextKey, label: '', stages: [] }])}>+ Add key option</button>
      </div>

      <OptionalBranch
        title="No key pressed"
        whenPresent="after the last play, route to:"
        whenAbsent="after the last play, continue with the stages after this menu"
        stages={noInput ? (noInput.stages ?? []) : null}
        onToggle={(present) => setCfg({ noInput: present ? { stages: [] } : null })}
        onChange={setNoInputStages}
        allowMenu={false}
      />
    </div>
  );
}

/** Time entries (days + start/end + their stages) and the "otherwise" branch. */
function TimeConfigEditor({ stage, onChange }: { stage: Stage; onChange: (update: Updater<Stage>) => void }) {
  const cfg = stage.config;
  const entriesOf = (st: Stage) => ((st.config['entries'] as TimeEntry[] | undefined) ?? []);
  const entries = entriesOf(stage);
  const otherwise = (cfg['otherwise'] as { stages: Stage[] } | null | undefined) ?? null;
  const setCfg = (patch: Record<string, unknown>) => onChange((prev) => ({ ...prev, config: { ...prev.config, ...patch } }));
  const setEntries = (f: Updater<TimeEntry[]>) => onChange((prev) => ({ ...prev, config: { ...prev.config, entries: f(entriesOf(prev)) } }));
  const setEntry = (i: number, patch: Partial<TimeEntry>) => setEntries((es) => es.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const setEntryStages = (i: number, f: Updater<Stage[]>) => setEntries((es) => es.map((e, j) => (j === i ? { ...e, stages: f(e.stages ?? []) } : e)));
  const moveEntry = (i: number, d: -1 | 1) => setEntries((es) => swap(es, i, d));
  const setOtherwiseStages = (f: Updater<Stage[]>) => onChange((prev) => {
    const cur = (prev.config['otherwise'] as { stages?: Stage[] } | null | undefined)?.stages ?? [];
    return { ...prev, config: { ...prev.config, otherwise: { stages: f(cur) } } };
  });
  const toggleDay = (i: number, day: number) => setEntries((es) => es.map((e, j) => {
    if (j !== i) return e;
    const days = e.days.includes(day) ? e.days.filter((d) => d !== day) : [...e.days, day].sort((a, b) => a - b);
    return { ...e, days };
  }));

  return (
    <div className="picker">
      <p className="sub">
        Decided when the call arrives, in UK time ({String(cfg['timeZone'] ?? 'Europe/London')}); entries are checked top to
        bottom and the first that matches decides. An end time before the start runs overnight into the <em>following</em> day,
        so “Mon–Fri 18:00–08:00” covers Friday night into Saturday morning but not Sunday night into Monday morning — tick Sun
        as well (or use Otherwise). The same start and end time means the whole of each ticked day.
      </p>
      {entries.length === 0 && <p className="hint">Add at least one time entry — a condition with no entries will not save.</p>}
      {entries.map((e, i) => (
        <div key={i} className="picker time-entry">
          <div className="filters">
            <span className="reorder">
              <button className="link" disabled={i === 0} onClick={() => moveEntry(i, -1)}>↑</button>
              <button className="link" disabled={i === entries.length - 1} onClick={() => moveEntry(i, 1)}>↓</button>
            </span>
            <input placeholder="Label (e.g. Office hours)" value={e.label} onChange={(ev) => setEntry(i, { label: ev.target.value })} />
            <label>from <input type="time" value={e.start} onChange={(ev) => setEntry(i, { start: ev.target.value })} /></label>
            <label>to <input type="time" value={e.end} onChange={(ev) => setEntry(i, { end: ev.target.value })} /></label>
            <span className="spacer" />
            <button className="link danger" onClick={() => setEntries((es) => es.filter((_, j) => j !== i))}>remove entry</button>
          </div>
          <div className="filters days">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <label key={d} className={`pill${e.days.includes(d) ? ' ok' : ''}`}>
                <input type="checkbox" checked={e.days.includes(d)} onChange={() => toggleDay(i, d)} /> {DAY_LABEL[d]}
              </label>
            ))}
            {e.days.length === 0 && <span className="error">pick at least one day</span>}
          </div>
          <StageList stages={e.stages ?? []} onChange={(f) => setEntryStages(i, f)} allowMenu compact />
          {(e.stages ?? []).length === 0 && <p className="hint">Add at least one stage to this entry — an entry with no stages will not save.</p>}
        </div>
      ))}
      <div className="filters">
        <button onClick={() => setEntries((es) => [...es, { label: '', days: WEEKDAYS, start: '08:00', end: '18:00', stages: [] }])}>+ Add time entry</button>
      </div>

      <OptionalBranch
        title="Otherwise"
        whenPresent="when no entry matches, route to:"
        whenAbsent="when no entry matches, continue with the stages after this condition"
        stages={otherwise ? (otherwise.stages ?? []) : null}
        onToggle={(present) => setCfg({ otherwise: present ? { stages: [] } : null })}
        onChange={setOtherwiseStages}
        allowMenu
      />
    </div>
  );
}

function ShiftGroupConfig({ config, onChange }: { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void }) {
  const api = useApi();
  const [q, setQ] = useState('');
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [groups, setGroups] = useState<SchedulingGroup[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const teamId = String(config['teamId'] ?? '');
  const loadGroups = useCallback((tid: string) => {
    api.get<SchedulingGroup[]>(`/api/admin/teams/${tid}/scheduling-groups`).then(setGroups, (e: Error) => setErr(e.message));
  }, [api]);
  useEffect(() => { if (teamId) loadGroups(teamId); }, [teamId, loadGroups]);

  return (
    <div className="picker">
      {err && <p className="error">{err}</p>}
      <div className="filters">
        <span className="sub">Team: <strong>{String(config['teamName'] ?? '—')}</strong></span>
        <input placeholder="Search Teams (min 2)" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && q.length >= 2 && api.get<Team[]>(`/api/admin/teams/search?q=${encodeURIComponent(q)}`).then(setTeams, (er: Error) => setErr(er.message))} />
        <button onClick={() => q.length >= 2 && api.get<Team[]>(`/api/admin/teams/search?q=${encodeURIComponent(q)}`).then(setTeams, (er: Error) => setErr(er.message))}>Search</button>
      </div>
      {teams && (
        <ul className="picker-results">
          {teams.map((t) => (
            <li key={t.id}><button className="link" onClick={() => { onChange({ ...config, teamId: t.id, teamName: t.displayName, schedulingGroupId: '', groupName: '' }); setTeams(null); }}>{t.displayName}</button></li>
          ))}
          {teams.length === 0 && <li><em>No Teams matched</em></li>}
        </ul>
      )}
      {teamId && groups && groups.length === 0 && (
        <p className="hint">This team has no active Shifts groups — pick another team, or add a group in Teams &gt; Shifts.</p>
      )}
      {teamId && groups && groups.length > 0 && (
        <div className="filters">
          <span className="sub">Group:</span>
          <select value={String(config['schedulingGroupId'] ?? '')} onChange={(e) => {
            const g = groups.find((x) => x.id === e.target.value);
            onChange({ ...config, schedulingGroupId: e.target.value, groupName: g?.displayName ?? '' });
          }}>
            <option value="">Select group…</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.displayName} ({g.members} members)</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
